import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    LabelList,
    ResponsiveContainer,
    Cell,
    CartesianGrid,
    ReferenceLine,
} from 'recharts';
import { roundHalfUp } from '../../pages/selfcare/selfcareReportLogic';

const PROJECTED_COLOR = '#62C190';
const META_COLOR = '#20B2AA';

/**
 * ICP Trend Chart for Managerial Cross General report
 * Shows historical bars (traffic light colors) + projected bars (#62C190) + target line
 */
const ManCrossIcpTrendChart = ({ data = [] }) => {
    if (!data || data.length === 0) return null;

    const getColor = (value) => {
        if (value >= 80) return '#1E8449';
        if (value >= 65) return '#F9A825';
        if (value >= 50) return '#E67E22';
        return '#C0392B';
    };

    const parsedData = data.map((item) => ({
        period: item.period,
        value: Number.isFinite(Number(item.value)) ? Number(item.value) : 0,
        isProjected: Boolean(item.isProjected),
        color: item.isProjected ? PROJECTED_COLOR : getColor(item.value),
    }));

    const height = 280;

    const renderValueLabel = (props) => {
        const { x, y, width, height: barHeight, value } = props;
        const v = Number(value);

        const cx = (x || 0) + (width || 0) / 2;
        const cy = (y || 0) - 8;

        return (
            <text
                x={cx}
                y={cy}
                fill="#334155"
                fontSize={10}
                fontWeight={800}
                dominantBaseline="middle"
                textAnchor="middle"
            >
                {`${Number.isFinite(v) ? roundHalfUp(v, 1) : 0}`}
            </text>
        );
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div style={{ backgroundColor: 'white', padding: 10, border: '1px solid #ccc' }}>
                    <div style={{ fontWeight: 700 }}>{data.period}</div>
                    <div style={{ fontWeight: 600, color: data.color }}>
                        {roundHalfUp(data.value, 1)}%
                    </div>
                    {data.isProjected && (
                        <div style={{ fontSize: 11, color: '#5b5b5b' }}>Proyectado</div>
                    )}
                </div>
            );
        }
        return null;
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={parsedData} margin={{ top: 32, right: 20, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#e8e8e8" vertical horizontal={false} />

                <XAxis
                    dataKey="period"
                    tick={{ fontSize: 10, fill: '#5b5b5b' }}
                    axisLine={false}
                    tickLine={false}
                />

                <YAxis
                    type="number"
                    domain={[0, 100]}
                    ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                    tick={{ fontSize: 10, fill: '#8c8c8c' }}
                    tickFormatter={(v) => `${v}%`}
                    axisLine={false}
                    tickLine={false}
                />

                <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1000 }} />

                <ReferenceLine
                    y={85}
                    stroke={META_COLOR}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={{
                        value: 'Meta',
                        position: 'right',
                        fill: META_COLOR,
                        fontSize: 10,
                        fontWeight: 700,
                        offset: 6,
                    }}
                />

                <Bar dataKey="value" barSize={32} radius={[8, 8, 0, 0]} isAnimationActive={false}>
                    {parsedData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                    <LabelList
                        dataKey="value"
                        content={renderValueLabel}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default ManCrossIcpTrendChart;
